// Copied from platform-app components/pos/receipt/webUsbPrinter.ts on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
/**
 * WebUSB transport for ESC/POS thermal printers (RONGTA-class).
 *
 * Browser support: Chrome, Edge, Opera (NOT Safari, NOT Firefox).
 * Permission model: user grants access once via navigator.usb.requestDevice;
 * subsequent sessions can pick the device up via navigator.usb.getDevices().
 *
 * Detected RONGTA on this dev Mac: VID 0x0fe6, PID 0x811e.
 * Other common RONGTA models: 0x0416 / 0x5011 (RP58), 0x0fe6 / 0x811e (RP80).
 * The filter below is permissive — `classCode: 7` matches any USB Printer Class
 * device, plus the explicit RONGTA VIDs as a hint to the picker.
 */

// POS Defaults: super-admin-ui has no @types/w3c-web-usb (and nothing may be
// installed) — minimal local aliases instead.
/* eslint-disable @typescript-eslint/no-explicit-any */
type USB = any;
type USBDevice = any;
type USBDeviceFilter = any;

const RONGTA_VENDOR_IDS = [0x0fe6, 0x0416];

declare global {
  interface Navigator {
    usb?: USB;
  }
}

interface ConfiguredDevice {
  device: USBDevice;
  endpointOut: number;        // bulk OUT endpoint number
  interfaceNumber: number;    // claimed interface
}

let cached: ConfiguredDevice | null = null;

export function isWebUsbSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.usb;
}

/**
 * Open + claim a previously-authorized device. Returns null if none paired.
 * Call this on app boot to silently restore the connection.
 */
export async function tryRestoreDevice(): Promise<ConfiguredDevice | null> {
  if (!isWebUsbSupported()) return null;
  const devices = await navigator.usb!.getDevices();
  if (devices.length === 0) return null;
  // Prefer a printer-class device, else first one
  const printerClass = devices.find((d) => d.deviceClass === 7) || devices[0];
  try {
    cached = await openDevice(printerClass);
    return cached;
  } catch (err) {
    console.warn('[webusb] could not restore device, will require re-pairing', err);
    return null;
  }
}

/**
 * Prompt the user with the browser's USB device picker. The picker is gated
 * by user gesture — call this from inside a click handler.
 */
export async function requestDevice(): Promise<ConfiguredDevice> {
  if (!isWebUsbSupported()) {
    throw new Error('WebUSB not supported in this browser. Use Chrome or Edge.');
  }
  const filters: USBDeviceFilter[] = [
    { classCode: 7 },                                     // USB Printer Class
    ...RONGTA_VENDOR_IDS.map((vendorId) => ({ vendorId })),
  ];
  const device = await navigator.usb!.requestDevice({ filters });
  cached = await openDevice(device);
  return cached;
}

// Open the device and locate its bulk OUT endpoint, but do NOT claim the
// interface here. Holding the claim across prints would lock out other tabs
// or apps that have paired with the same printer (only one browsing context
// can claim a USB interface at a time). sendBytes() claims transiently below.
async function openDevice(device: USBDevice): Promise<ConfiguredDevice> {
  if (!device.opened) await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);

  const config = device.configuration!;
  let interfaceNumber = -1;
  let endpointOut = -1;
  for (const iface of config.interfaces) {
    for (const alt of iface.alternates) {
      const out = alt.endpoints.find((ep) => ep.direction === 'out' && ep.type === 'bulk');
      if (out) {
        interfaceNumber = iface.interfaceNumber;
        endpointOut = out.endpointNumber;
        break;
      }
    }
    if (interfaceNumber !== -1) break;
  }
  if (interfaceNumber === -1 || endpointOut === -1) {
    throw new Error('No bulk OUT endpoint found on device — not a printer?');
  }
  return { device, endpointOut, interfaceNumber };
}

// Claim → run → release. Lets multiple browser tabs share the same printer.
async function withClaimedInterface<T>(d: ConfiguredDevice, fn: () => Promise<T>): Promise<T> {
  let lastErr: any = null;
  const delays = [200, 400, 800, 1600, 3200];
  for (let attempt = 0; attempt < delays.length; attempt++) {
    try {
      await d.device.claimInterface(d.interfaceNumber);
      try {
        return await fn();
      } finally {
        try { await d.device.releaseInterface(d.interfaceNumber); } catch { /* ignore */ }
      }
    } catch (err) {
      lastErr = err;
      console.warn(`[webusb] claim attempt ${attempt + 1}/${delays.length} failed; retrying in ${delays[attempt]}ms`);
      await new Promise((r) => setTimeout(r, delays[attempt]));
    }
  }
  throw new Error(
    'Could not claim the USB printer — another tab or window is holding it. ' +
    'FIX: Hard-refresh every tab that has this printer paired (Cmd+Shift+R / Ctrl+Shift+F5), ' +
    'OR close all other tabs/apps using the printer, then try again. ' +
    `Underlying: ${lastErr instanceof Error ? lastErr.message : String(lastErr)}`
  );
}

/**
 * Send raw ESC/POS bytes to the printer. Splits writes into ~1KB chunks for
 * compatibility, claims the interface only for the duration of the transfer.
 */
export async function sendBytes(bytes: Uint8Array): Promise<void> {
  if (!cached) {
    const restored = await tryRestoreDevice();
    if (!restored) throw new Error('No printer paired. Click "Connect Printer" first.');
    cached = restored;
  }
  if (!cached) throw new Error('No printer device available');
  const d = cached;
  await withClaimedInterface(d, async () => {
    const CHUNK = 1024;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      const slice = bytes.slice(i, i + CHUNK);
      const result = await d.device.transferOut(d.endpointOut, slice);
      if (result.status !== 'ok') {
        throw new Error(`USB transfer failed: ${result.status}`);
      }
    }
  });
}

/** Close the device — call on unmount/sign-out. */
export async function disconnect(): Promise<void> {
  if (!cached) return;
  try {
    await cached.device.close();
  } catch (err) {
    console.warn('[webusb] disconnect error', err);
  } finally {
    cached = null;
  }
}

export function getCachedDevice(): ConfiguredDevice | null {
  return cached;
}
